import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowUpRight, CheckCircle2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const FIELDS = [
  { k: "name", label: "Name", required: true },
  { k: "business", label: "Business name", required: true },
  { k: "website", label: "Website", placeholder: "yourbusiness.com" },
  { k: "email", label: "Email", type: "email", required: true },
  { k: "phone", label: "Phone (optional)", type: "tel" },
  { k: "town", label: "Town", placeholder: "Gallatin, Hendersonville…" },
];
const EMPTY = { name: "", business: "", website: "", email: "", phone: "", town: "", wants: "" };

export const AuditRequestForm = ({ replyNote }) => {
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const details = [
      `Website: ${form.website || "—"}`,
      `Phone: ${form.phone || "—"}`,
      `Town: ${form.town || "—"}`,
      `Wants more of: ${form.wants || "—"}`,
    ].join("\n");
    try {
      await axios.post(`${API}/contact`, {
        name: form.name,
        email: form.email,
        company: form.business,
        interest: "Free Local AI Audit",
        details,
      });
      toast.success("Audit request received. You'll hear from Robert directly.");
      setSent(true);
      setForm(EMPTY);
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : d?.[0]?.msg || "Something broke. Try again in a sec.");
    } finally {
      setBusy(false);
    }
  };

  if (sent)
    return (
      <div className="flex items-start gap-3 font-body text-white/85" data-testid="audit-form-success">
        <CheckCircle2 className="text-[var(--ggm-signal-cyan)] shrink-0" />
        <p>Got it. Your audit is in the queue, and your report will land in your inbox within 3 business days.</p>
      </div>
    );

  return (
    <form onSubmit={submit} className="grid gap-4 md:grid-cols-2" data-testid="audit-request-form">
      {FIELDS.map((f) => (
        <label key={f.k} className="flex flex-col gap-2">
          <span className="font-condensed uppercase tracking-widest text-xs text-white/70">{f.label}</span>
          <input
            type={f.type || "text"}
            required={f.required}
            value={form[f.k]}
            onChange={set(f.k)}
            placeholder={f.placeholder}
            className="ggm-input"
            data-testid={`audit-form-${f.k}`}
          />
        </label>
      ))}
      <label className="flex flex-col gap-2 md:col-span-2">
        <span className="font-condensed uppercase tracking-widest text-xs text-white/70">
          What would you like more of? (calls, bookings, quotes…)
        </span>
        <textarea rows={3} value={form.wants} onChange={set("wants")} className="ggm-input" data-testid="audit-form-wants" />
      </label>
      <div className="md:col-span-2 flex flex-wrap items-center gap-4">
        <button type="submit" disabled={busy} className="ggm-btn-primary" data-testid="audit-form-submit">
          {busy ? "Sending…" : "Get my free audit"} <ArrowUpRight size={16} />
        </button>
        <span className="font-body text-white/55 text-sm italic">{replyNote}</span>
      </div>
    </form>
  );
};
